import { useEffect, useMemo, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useMissatgeError } from '@/api/errors';
import {
  useImportaExcel,
  useUsuarisInformar,
  type ImportErrorDto,
  type ImportResultatDto,
} from '@/api/administracio';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { Buit } from '@/pages/operacio/comu';
import { CapPanell } from './comu';
import {
  CAMPS_IMPORT,
  CAMPS_OBLIGATORIS,
  llegeixExcel,
  mapeigComplet,
  suggereixMapeig,
  validaFiles,
  type CampImport,
  type FullExcel,
} from './excel';
import { opcionsServei, useEstructura } from './marc/estructura';
import { midaLlegible } from './utils';
import './administracio.css';
import './avisos.css';

const PASSOS = ['servei', 'fitxer', 'columnes', 'revisa'] as const;
const PASSOS_INDICADOR = [...PASSOS, 'importa'] as const;
/** RF-047: només s'admeten llibres d'Excel. */
const EXTENSIONS = ['xlsx', 'xls'];
const CODIS_CONEGUTS = ['obligatori', 'email', 'duplicat', 'telefon'];

export function W37Importacio() {
  const { t } = useTranslation('administracio');
  const missatgeError = useMissatgeError();
  const [params] = useSearchParams();
  const estructura = useEstructura();
  const importa = useImportaExcel();
  const valida = useImportaExcel();
  const inicialServei = params.get('servei') ? Number(params.get('servei')) : null;
  const [serveiId, setServeiId] = useState<number | null>(inicialServei);
  const [pas, setPas] = useState(inicialServei != null ? 1 : 0);
  const [fitxer, setFitxer] = useState<File | null>(null);
  const [full, setFull] = useState<FullExcel | null>(null);
  const [errorLectura, setErrorLectura] = useState(false);
  const [errorFormat, setErrorFormat] = useState(false);
  const [arrossegant, setArrossegant] = useState(false);
  const [mapeig, setMapeig] = useState<Partial<Record<CampImport, string>>>({});
  const [resultat, setResultat] = useState<ImportResultatDto | null>(null);
  const actuals = useUsuarisInformar(serveiId);

  const servei = estructura.servei(serveiId);
  const ruta = serveiId != null ? estructura.ruta('SERVEI', serveiId) : '';
  const local = useMemo(() => (full ? validaFiles(full.files, mapeig) : null), [full, mapeig]);
  const servidor = valida.data;
  const complet = mapeigComplet(mapeig);
  const valids = servidor?.valids ?? local?.valids ?? 0;
  const duplicats = servidor?.duplicats ?? local?.duplicats ?? 0;
  const errors: (ImportErrorDto | { fila: number; camp: string; codi: string })[] =
    servidor?.errors ?? local?.errors ?? [];
  const potAvancar = [serveiId != null, !!full && full.files.length > 0, complet, true][pas];

  const mapeigDto = () => ({
    nom: mapeig.nom ?? '',
    email: mapeig.email ?? '',
    telefon: mapeig.telefon ?? null,
    canals: mapeig.canals ?? null,
  });

  // En arribar a «Revisa» es valida al servidor amb el servei i el mapeig triats.
  useEffect(() => {
    if (pas !== 3 || !fitxer || serveiId == null || !complet) return;
    if (valida.isPending || valida.data || valida.isError) return;
    valida.mutate({ serveiId, fitxer, mapeig: mapeigDto(), validar: true });
    // mapeigDto depèn de `mapeig`, que ja és a les dependències.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pas, fitxer, serveiId, complet, mapeig]);

  const tria = async (f: File | undefined) => {
    setErrorLectura(false);
    setErrorFormat(false);
    valida.reset();
    if (!f) return;
    if (!EXTENSIONS.includes(f.name.split('.').pop()?.toLowerCase() ?? '')) {
      setErrorFormat(true);
      return;
    }
    try {
      const llegit = await llegeixExcel(f);
      setFitxer(f);
      setFull(llegit);
      setMapeig(suggereixMapeig(llegit.columnes));
    } catch {
      setErrorLectura(true);
    }
  };

  const deixa = (e: DragEvent) => {
    e.preventDefault();
    setArrossegant(false);
    void tria(e.dataTransfer.files?.[0]);
  };

  const executa = () => {
    if (!fitxer || serveiId == null) return;
    importa.mutate(
      { serveiId, fitxer, mapeig: mapeigDto(), validar: false },
      { onSuccess: setResultat },
    );
  };

  const reinicia = () => {
    setResultat(null);
    setFitxer(null);
    setFull(null);
    setMapeig({});
    valida.reset();
    importa.reset();
    setPas(1);
  };

  const textError = (e: { fila: number; camp: string; codi: string; missatge?: string | null }) =>
    CODIS_CONEGUTS.includes(e.codi)
      ? t(`importacio.errorFila.${e.codi}`, {
          fila: e.fila,
          camp: t(`importacio.camps.${e.camp}`, { defaultValue: e.camp }),
        })
      : t('avisos.importacio.errorGeneric', { fila: e.fila, text: e.missatge ?? e.codi });

  const enllacLlista =
    serveiId != null ? `/admin/usuaris-informar?servei=${serveiId}` : '/admin/usuaris-informar';

  return (
    <section className="pantalla" data-screen="W-37">
      <Toolbar titol={t('importacio.titol')} enrere={enllacLlista} />
      <div className="content importacio">
        <ol className="assistent-passos" aria-label={t('avisos.importacio.passos')}>
          {PASSOS_INDICADOR.map((p, i) => {
            const fet = resultat ? true : i < pas;
            const actual = resultat ? i === 4 : i === pas;
            return (
              <li
                key={p}
                className={`assistent-pas${fet ? ' is-fet' : ''}${actual ? ' is-actual' : ''}`}
                aria-current={actual ? 'step' : undefined}
              >
                <span className="assistent-pas__num">
                  {fet && !actual ? <Icona nom="checkmark" /> : i + 1}
                </span>
                {t(`avisos.importacio.pas.${p}`)}
              </li>
            );
          })}
        </ol>

        {resultat ? (
          <div className="panel" data-pas="resultat">
            <div className="panel__body form">
              <div className="info-banda" role="status">
                <Icona nom="checkmark-circle" />
                {t('importacio.resultat', { ...resultat })}
              </div>
              <div className="row">
                <Link to={enllacLlista} className="btn btn--dark btn--s">
                  <Icona nom="person-2" />
                  {t('avisos.importacio.veureLlista')}
                </Link>
                <Link to={`/admin/arees?servei=${serveiId}`} className="btn btn--s">
                  <Icona nom="building" />
                  {t('avisos.importacio.obreServei')}
                </Link>
                <button type="button" className="btn btn--s" onClick={reinicia}>
                  <Icona nom="arrow-clockwise" />
                  {t('avisos.importacio.unAltre')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="panel" data-pas={PASSOS[pas]}>
            <CapPanell titol={t(`avisos.importacio.titolPas.${PASSOS[pas]}`)} />
            <div className="panel__body form">
              {pas === 0 ? (
                <>
                  <p className="t-footnote dim" style={{ margin: 0 }}>
                    {t('avisos.importacio.ajudaServei')}
                  </p>
                  <div className="camp" style={{ maxWidth: 520 }}>
                    <span className="camp__label">{t('importacio.servei')}</span>
                    <Desplegable<number>
                      aria-label={t('importacio.servei')}
                      valor={serveiId ?? ''}
                      placeholder={t('avisos.importacio.triaServei')}
                      opcions={opcionsServei(estructura, { seleccionat: serveiId })}
                      onCanvia={(v) => {
                        setServeiId(v);
                        valida.reset();
                      }}
                    />
                  </div>
                  {serveiId != null ? (
                    <p className="t-footnote dim" style={{ margin: 0 }}>
                      {actuals.isLoading
                        ? t('comu.carregant')
                        : t('avisos.importacio.llistaActual', {
                            count: actuals.data?.length ?? 0,
                          })}
                    </p>
                  ) : null}
                </>
              ) : null}

              {pas === 1 ? (
                <>
                  <label
                    className={`zona-fitxer${arrossegant ? ' is-sobre' : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setArrossegant(true);
                    }}
                    onDragLeave={() => setArrossegant(false)}
                    onDrop={deixa}
                  >
                    <Icona nom="paperclip" />
                    <div className="zona-fitxer__titol">{t('documents.arrossega')}</div>
                    <div className="t-caption dim mt-4">{t('importacio.ajudaFitxer')}</div>
                    <input
                      type="file"
                      aria-label={t('importacio.fitxer')}
                      accept=".xlsx,.xls"
                      onChange={(e) => void tria(e.target.files?.[0])}
                    />
                  </label>
                  {errorFormat ? (
                    <p className="error-text">{t('importacio.formatNoValid')}</p>
                  ) : null}
                  {errorLectura ? (
                    <p className="error-text">{t('importacio.errorLectura')}</p>
                  ) : null}
                  {full && fitxer ? (
                    <div className="fitxer-fila" role="status">
                      <Icona nom="doc-text" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="truncate">{fitxer.name}</div>
                        <div className="t-caption-2 dim">
                          {midaLlegible(fitxer.size)} ·{' '}
                          {t('importacio.llegides', {
                            nom: fitxer.name,
                            files: full.files.length,
                            columnes: full.columnes.length,
                          })}
                        </div>
                      </div>
                      <Icona nom="checkmark-circle" />
                    </div>
                  ) : null}
                </>
              ) : null}

              {pas === 2 && full ? (
                <>
                  <table className="taula">
                    <thead>
                      <tr>
                        <th>{t('importacio.campPlataforma')}</th>
                        <th>{t('importacio.columnaExcel')}</th>
                        <th>{t('importacio.exemple')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CAMPS_IMPORT.map((c) => {
                        const col = mapeig[c];
                        return (
                          <tr key={c}>
                            <td>
                              <span className={`chip${col ? ' chip--blue' : ''}`}>
                                {t(`importacio.camps.${c}`)}
                                {CAMPS_OBLIGATORIS.includes(c) ? ' *' : ''}
                              </span>
                            </td>
                            <td style={{ minWidth: 220 }}>
                              <Desplegable<string>
                                variant="compacte"
                                aria-label={t(`importacio.camps.${c}`)}
                                valor={col ?? ''}
                                opcions={[
                                  { valor: '', etiqueta: t('importacio.noImportar') },
                                  ...full.columnes.map((columna) => ({
                                    valor: columna,
                                    etiqueta: columna,
                                  })),
                                ]}
                                onCanvia={(v) => {
                                  setMapeig({ ...mapeig, [c]: v || undefined });
                                  valida.reset();
                                }}
                              />
                            </td>
                            <td className="dim">{col ? (full.files[0]?.[col] ?? '—') : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {!complet ? (
                    <p className="error-text">{t('importacio.mapeigIncomplet')}</p>
                  ) : null}
                </>
              ) : null}

              {pas === 3 && local ? (
                <>
                  <dl className="importacio-resum">
                    <dt>{t('importacio.servei')}</dt>
                    <dd>
                      {servei?.nom ?? '—'} {ruta ? <span className="dim">· {ruta}</span> : null}
                    </dd>
                    <dt>{t('importacio.fitxer')}</dt>
                    <dd>{fitxer?.name}</dd>
                    <dt>{t('importacio.valides')}</dt>
                    <dd className="c-green" data-kpi="valids">
                      {valids}
                    </dd>
                    <dt>{t('importacio.duplicades')}</dt>
                    <dd className="c-orange">
                      {t('avisos.importacio.sOmeten', { count: duplicats })}
                    </dd>
                    <dt>{t('importacio.errors')}</dt>
                    <dd className={errors.length ? 'c-red' : ''} data-kpi="errors">
                      {errors.length}
                    </dd>
                  </dl>
                  <p className="t-caption dim" role="status" style={{ margin: 0 }}>
                    {valida.isPending
                      ? t('importacio.validantServidor')
                      : servidor
                        ? t('importacio.validatServidor')
                        : t('avisos.importacio.validacioLocal')}
                  </p>
                  {valida.isError ? (
                    <div className="row">
                      <p className="error-text" style={{ margin: 0 }}>
                        {missatgeError(valida.error)}
                      </p>
                      <button
                        type="button"
                        className="btn btn--xs"
                        onClick={() => {
                          if (fitxer && serveiId != null)
                            valida.mutate({ serveiId, fitxer, mapeig: mapeigDto(), validar: true });
                        }}
                      >
                        <Icona nom="arrow-clockwise" />
                        {t('avisos.importacio.revalida')}
                      </button>
                    </div>
                  ) : null}
                  {errors.length ? (
                    <details className="importacio-errors">
                      <summary>
                        {t('avisos.importacio.veureErrors', { count: errors.length })}
                      </summary>
                      <ul>
                        {errors.map((e, i) => (
                          <li key={`${e.fila}-${e.camp}-${e.codi}-${i}`}>{textError(e)}</li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                  <div className="info-banda">
                    <Icona nom="info-circle" />
                    {t('avisos.importacio.mode', {
                      count: valids,
                      actuals: actuals.data?.length ?? 0,
                    })}
                  </div>
                  {importa.isError ? (
                    <p className="error-text">{missatgeError(importa.error)}</p>
                  ) : null}
                </>
              ) : null}
              {pas === 3 && !local ? (
                <Buit icona="doc-text">{t('importacio.primerFitxer')}</Buit>
              ) : null}

              <div className="row assistent-peu">
                {pas === 0 ? (
                  <Link to={enllacLlista} className="btn btn--s">
                    {t('comu.cancella')}
                  </Link>
                ) : (
                  <button type="button" className="btn btn--s" onClick={() => setPas(pas - 1)}>
                    <Icona nom="chevron-left" />
                    {t('comu.enrere')}
                  </button>
                )}
                <div className="spacer" />
                {pas < 3 ? (
                  <button
                    type="button"
                    className="btn btn--dark btn--s"
                    disabled={!potAvancar}
                    onClick={() => setPas(pas + 1)}
                  >
                    {t('comu.seguent')}
                    <Icona nom="chevron-right" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn--dark btn--s"
                    disabled={
                      !fitxer || !complet || serveiId == null || valids === 0 || importa.isPending
                    }
                    onClick={executa}
                  >
                    <Icona nom="arrow-up" />
                    {t('importacio.importaN', { count: valids })}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
